import React from 'react';

import { UNMAPPED_ROOM_KEY } from '../PackageSetupStep/roomMapping';
import RoomMappingPicker from '../RoomMappingPicker';
import type { RoomMappingRow } from '../RoomMappingPicker';

import DealOptionsDisclosure from './DealOptionsDisclosure';
import type { RoomAssetRow, RoomSource } from './roomAssetMapping';
import type { PackageRoomOption } from './utils';

const ROOM_SOURCE_CHIPS: Record<RoomSource, RoomMappingRow['chip']> = {
  ai: { color: 'info', label: 'AI SUGGESTED' },
  manual: { label: 'MANUAL' },
};

interface Props {
  disabled?: boolean;
  onSelectRoom: (assetId: string, roomKey: string) => void;
  roomAssetRows: Array<RoomAssetRow>;
  roomOptions: Array<PackageRoomOption>;
  selectedRoomKeyByAssetId: Record<string, string>;
  selectedRoomSourceByAssetId: Record<string, RoomSource>;
}

export default function RoomAssetPicker({
  disabled,
  onSelectRoom,
  roomAssetRows,
  roomOptions,
  selectedRoomKeyByAssetId,
  selectedRoomSourceByAssetId,
}: Props) {
  if (roomAssetRows.length === 0) {
    return null;
  }

  const rows = roomAssetRows.map((roomAsset): RoomMappingRow => {
    const roomKey = selectedRoomKeyByAssetId[roomAsset.assetId] ?? UNMAPPED_ROOM_KEY;
    const roomSource = selectedRoomSourceByAssetId[roomAsset.assetId];

    return {
      chip: roomSource && ROOM_SOURCE_CHIPS[roomSource],
      errorText: roomKey === UNMAPPED_ROOM_KEY ? 'Choose a room to continue' : undefined,
      id: roomAsset.assetId,
      roomKey,
      subtitle: <DealOptionsDisclosure names={roomAsset.dealOptionNames} />,
      title: roomAsset.assetName,
    };
  });

  return (
    <RoomMappingPicker
      description={[
        'Each Salesforce room was matched to a room by AI. All mappings must be confirmed before continuing.',
        <>
          Rooms shown as <strong>(new)</strong> will be created when you submit the offer. You can still override the
          room for each package later, on the Package Setup step.
        </>,
      ]}
      disabled={disabled}
      onSelectRoom={onSelectRoom}
      roomOptions={roomOptions}
      rows={rows}
      selectIdPrefix="room-asset"
      title="Salesforce Room Asset mapping"
    />
  );
}
